<!--
  StateMachineWizardModal.vue

  Purpose: Wizard modal component driven EXCLUSIVELY by an XState machine.

  Architecture:
  - XState machine is the SINGLE SOURCE OF TRUTH for all wizard state
  - NO internal step counter - all state derived from machine
  - Button clicks directly call machine methods
  - Step indicators reflect machine's current step index

  Key Differences from WizardModal.vue:
  - WizardModal has useWizard composable with internal state (can desync)
  - StateMachineWizardModal has NO internal state - pure derivation from machine

  Usage:
  ```vue
  <StateMachineWizardModal
    :machine="payrollWizardMachine"
    :show-modal="isVisible"
    modal-title="Payroll Wizard"
    @close="handleClose"
  />
  ```

  The machine prop must expose:
  - visibleSteps: computed array of { key, title } objects
  - currentStepIndex: computed number (0-based)
  - currentStepComponent: computed Vue component for current step
  - isLoading: computed boolean
  - canProceed: computed boolean
  - canGoBack: computed boolean
  - getNextLabelForStep(index): function returning button label
  - next(): function to advance
  - back(): function to go back
  - confirm(): function for final confirmation
  - cancel(): function to cancel/reset
-->
<template>
  <Modal
    :show="showModal"
    :title="modalTitle"
    :elem-id="elemId"
    :size="size"
    :close-label="backButtonLabel"
    :close-icon="backButtonIcon"
    :save-label="nextButtonLabel"
    :save-icon="nextButtonIcon"
    :save-fn="handleNextButton"
    :save-disabled-fn="isNextDisabled"
    @close="handleBackButton"
    @dismiss="handleDismiss"
  >
    <div class="state-machine-wizard-content">
      <!-- Summary section (optional) -->
      <div v-if="showSummary && summaryItems?.length > 0" class="wizard-summary">
        <div class="summary-row">
          <div v-for="(item, index) in summaryItems" :key="index" class="summary-item">
            <i v-if="item.icon" :class="item.icon"></i>
            <span class="summary-label">{{ item.label }}:</span>
            <span class="summary-value">{{ item.value }}</span>
          </div>
        </div>
      </div>

      <!-- Step indicators and content -->
      <WizardSteps
        :steps="visibleSteps"
        :current-step="currentStepIndex"
        :show-description="showStepDescription"
        :allow-click="allowStepClick"
        :orientation="orientation"
        @step-click="handleStepClick"
      >
        <!-- Render current step component dynamically -->
        <template v-for="(step, index) in visibleSteps" :key="step.key" #[`step-${index}`]>
          <component
            v-if="index === currentStepIndex && currentStepComponent"
            :is="currentStepComponent"
            :key="`${step.key}-${currentMachineState}`"
          />
          <div v-else-if="index === currentStepIndex && !currentStepComponent" class="step-loading-placeholder">
            <i class="pi pi-spinner pi-spin"></i>
            <span>{{ t('Carregando...') }}</span>
          </div>
        </template>
      </WizardSteps>
    </div>
  </Modal>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Modal from './Modal.vue';
import WizardSteps from './wizard/WizardSteps.vue';
import { log } from '@/utils/DebugLogger.ts';

interface WizardStepDef {
  key: string;
  title: string;
}

interface SummaryItem {
  icon?: string;
  label: string;
  value: string | number;
}

type ModalSize = 'small' | 'medium' | 'modalMd' | 'large' | 'xlarge' | 'full' | 'compactDesktop' | 'formOptimized';

interface StateMachine {
  visibleSteps: { value: WizardStepDef[] };
  currentStepIndex: { value: number };
  currentStepComponent?: { value: any };
  currentState: { value: string };
  isLoading: { value: boolean };
  canProceed: { value: boolean };
  canGoBack?: { value: boolean };
  isReadonlyMode?: { value: boolean };
  getNextLabelForStep?: (index: number) => string;
  next: () => void;
  back: () => void;
  confirm: () => void;
  cancel: () => void;
  goToStep?: (index: number) => void;
}

const { t } = useI18n();

// =============================================================================
// PROPS
// =============================================================================

/**
 * XState machine instance from usePayrollWizardMachine() or similar
 *
 * Required interface:
 * - visibleSteps: computed array of { key, title }
 * - currentStepIndex: computed number
 * - currentStepComponent: computed Component
 * - isLoading: computed boolean
 * - canProceed: computed boolean
 * - canGoBack: computed boolean
 * - currentState: computed string (machine state name)
 * - getNextLabelForStep(index): string
 * - next(): void
 * - back(): void
 * - confirm(): void
 * - cancel(): void
 */
const props = withDefaults(defineProps<{
  machine: StateMachine;
  // Modal configuration
  showModal: boolean;
  modalTitle?: string;
  size?: ModalSize;
  elemId?: string;
  // Visual configuration
  orientation?: 'horizontal' | 'vertical';
  showStepDescription?: boolean;
  allowStepClick?: boolean;
  // Summary section
  showSummary?: boolean;
  summaryItems?: SummaryItem[];
}>(), {
  modalTitle: '',
  size: 'xlarge',
  elemId: '',
  orientation: 'vertical',
  showStepDescription: true,
  allowStepClick: false,
  showSummary: false,
  summaryItems: () => [],
});

const emit = defineEmits<{
  close: [];
  cancel: [];
}>();

// =============================================================================
// COMPUTED - ALL DERIVED FROM MACHINE (NO INTERNAL STATE!)
// =============================================================================

/**
 * Visible steps array from machine
 * The machine determines which steps are visible based on context (rules, etc.)
 */
const visibleSteps = computed(() => props.machine.visibleSteps.value);

/**
 * Current step index from machine
 * This is THE source of truth for which step we're on
 */
const currentStepIndex = computed(() => props.machine.currentStepIndex.value);

/**
 * Current step component from machine
 * The machine maps its state to the appropriate Vue component
 */
const currentStepComponent = computed(() => props.machine.currentStepComponent?.value || null);

/**
 * Current machine state name (for logging/debugging)
 */
const currentMachineState = computed(() => props.machine.currentState.value);

/**
 * Is the machine in a loading/processing state
 */
const isLoading = computed(() => props.machine.isLoading.value);

/**
 * Can proceed to next step (from machine guards)
 */
const canProceed = computed(() => props.machine.canProceed.value);

/**
 * Can go back (from machine)
 */
const canGoBack = computed(() => props.machine.canGoBack?.value ?? true);

/**
 * Is this the first visible step
 */
const isFirstStep = computed(() => currentStepIndex.value === 0);

/**
 * Is this the last visible step
 * NOTE: We check machine state, not just index!
 */
const isLastStep = computed(() => {
  // Check if machine is in a final-ish state
  const finalStates = ['summary', 'finished', 'confirming'];
  return finalStates.includes(currentMachineState.value);
});

/**
 * Is machine in finished state
 */
const isFinished = computed(() => currentMachineState.value === 'finished');

// =============================================================================
// BUTTON LABELS AND ICONS - DERIVED FROM MACHINE STATE
// =============================================================================

const backButtonLabel = computed(() => {
  if (isFirstStep.value) {
    return t('Cancelar');
  }
  return t('Voltar');
});

const backButtonIcon = computed(() => {
  if (isFirstStep.value) {
    return 'pi pi-times';
  }
  return 'pi pi-arrow-left';
});

const nextButtonLabel = computed(() => {
  if (isLoading.value) {
    return t('Processando...');
  }

  // Use machine's label function if available
  if (props.machine.getNextLabelForStep) {
    return props.machine.getNextLabelForStep(currentStepIndex.value);
  }

  // Fallback based on state
  if (isFinished.value) {
    return t('Fechar');
  }
  if (currentMachineState.value === 'summary') {
    return t('Confirmar');
  }
  return t('Próximo');
});

const nextButtonIcon = computed(() => {
  if (isFinished.value) {
    return 'pi pi-check';
  }
  if (currentMachineState.value === 'summary') {
    return 'pi pi-check';
  }
  return 'pi pi-arrow-right';
});

// =============================================================================
// EVENT HANDLERS - DELEGATE DIRECTLY TO MACHINE
// =============================================================================

/**
 * Handle back/cancel button click
 * - On first step: cancel the wizard
 * - Otherwise: go back via machine
 */
const handleBackButton = (): void => {
  log('StateMachineWizardModal', 'handleBackButton', 'Back button clicked', {
    currentStep: currentStepIndex.value,
    machineState: currentMachineState.value,
    isFirstStep: isFirstStep.value
  });

  if (isFirstStep.value) {
    // Cancel the wizard
    handleCancel();
  } else {
    // Go back via machine
    props.machine.back();
  }
};

/**
 * Handle next/confirm button click
 * - Delegates to machine.next() or machine.confirm() based on state
 * - Machine guards will prevent invalid transitions
 */
const handleNextButton = (): void => {
  const callId = Math.random().toString(36).substring(7);

  log('StateMachineWizardModal', 'handleNextButton', `Next button clicked [${callId}]`, {
    currentStep: currentStepIndex.value,
    currentMachineState: currentMachineState.value,
    canProceed: canProceed.value,
    isLoading: isLoading.value
  });

  // Don't do anything if loading
  if (isLoading.value) {
    log('StateMachineWizardModal', 'handleNextButton', 'Blocked - loading');
    return;
  }

  // If finished, just close
  if (isFinished.value) {
    log('StateMachineWizardModal', 'handleNextButton', 'Finished - closing');
    handleClose();
    return;
  }

  // If in review or summary state, confirm
  if (currentMachineState.value === 'summary' || currentMachineState.value === 'review') {
    log('StateMachineWizardModal', 'handleNextButton', 'Summary/Review - calling confirm()');
    props.machine.confirm();
    return;
  }

  // Otherwise, go next
  log('StateMachineWizardModal', 'handleNextButton', 'Calling next()');
  props.machine.next();
};

/**
 * Check if next button should be disabled
 * Returns function for Modal's saveDisabledFn prop
 */
const isNextDisabled = (): boolean => {
  if (isLoading.value) {
    return true;
  }
  if (isFinished.value) {
    return false; // "Close" button should never be disabled
  }

  // Check if machine has readonly mode (e.g., payment rules viewing)
  // Machines can expose isReadonlyMode to disable confirm button
  if (props.machine.isReadonlyMode?.value) {
    return true;
  }

  return !canProceed.value;
};

/**
 * Handle modal dismiss (X button, outside click, Escape)
 */
const handleDismiss = (): void => {
  log('StateMachineWizardModal', 'handleDismiss', 'Modal dismissed');
  handleCancel();
};

/**
 * Handle cancel - reset machine and close
 */
const handleCancel = (): void => {
  log('StateMachineWizardModal', 'handleCancel', 'Cancelling wizard');
  props.machine.cancel();
  emit('cancel');
  emit('close');
};

/**
 * Handle close (after finished)
 */
const handleClose = (): void => {
  log('StateMachineWizardModal', 'handleClose', 'Closing wizard');
  emit('close');
};

/**
 * Handle step click (if allowed)
 * NOTE: For machine-driven wizards, clicking steps may not be supported
 */
const handleStepClick = (stepIndex: number): void => {
  log('StateMachineWizardModal', 'handleStepClick', 'Step clicked', { stepIndex });

  // Machine-driven wizards typically don't support arbitrary step navigation
  // But we could implement this if the machine supports it
  if (props.allowStepClick && props.machine.goToStep) {
    props.machine.goToStep(stepIndex);
  }
};

// =============================================================================
// EXPOSE FOR PARENT ACCESS (if needed)
// =============================================================================

defineExpose({
  currentStepIndex,
  currentMachineState,
  visibleSteps
});
</script>

<style scoped>
.state-machine-wizard-content {
  padding: 20px;
  background-color: #FFFFFF;
  border-radius: 8px;
  margin-bottom: 20px;
}

/* Summary Styling */
.wizard-summary {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 20px;
  border-left: 4px solid var(--app-info-color);
}

.summary-row {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  align-items: center;
}

.summary-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.875rem;
}

.summary-item i {
  font-size: 0.875rem;
  color: var(--app-info-color);
  flex-shrink: 0;
}

.summary-label {
  font-weight: 600;
  color: rgba(0, 0, 0, 0.6);
  white-space: nowrap;
}

.summary-value {
  font-weight: 500;
  color: rgba(0, 0, 0, 0.87);
  white-space: nowrap;
}

/* Responsive */
@media (max-width: 768px) {
  .state-machine-wizard-content {
    padding: 10px;
  }

  .wizard-summary {
    padding: 10px 12px;
  }

  .summary-row {
    gap: 12px;
  }

  .summary-item {
    font-size: 0.8125rem;
  }
}

/* Step Loading Placeholder */
.step-loading-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 60px 20px;
  color: rgba(0, 0, 0, 0.6);
  font-size: 1rem;
}

.step-loading-placeholder i {
  font-size: 1.5rem;
  color: var(--app-primary-color, #007bff);
}
</style>
